import { useCallback, useState } from "react";

interface UseImageDownloadReturn {
  downloadImage: (imageUrl: string, filename: string) => Promise<void>;
  isDownloading: boolean;
}

const useCloudinaryImageDownload = (): UseImageDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const injectTransformations = (url: string): string => {
    const [prefix, suffix] = url.split("/upload/");
    // Web optimization parameters:
    // f_auto: Automatic format selection (WebP where supported, JPEG fallback)
    // q_auto:best: Automatic quality with preference for best quality
    // w_1000: Maximum width of 1000px for web use
    // dpr_auto: Automatic DPR (device pixel ratio) detection
    const transformations = "f_auto,q_auto:best,w_1000,dpr_auto";

    return `${prefix}/upload/${transformations}/${suffix}`;
  };

  const downloadImage = useCallback(
    async (imageUrl: string, filename: string) => {
      if (!imageUrl) return;

      setIsDownloading(true);
      try {
        const optimizedUrl = injectTransformations(imageUrl);
        const response = await fetch(optimizedUrl);

        if (!response.ok)
          throw new Error(`Failed to download image: ${response.statusText}`);

        const blob = await response.blob();
        const contentType = response.headers.get("content-type") || "";
        const extension =
          contentType.match(/image\/(\w+)/)?.[1]?.replace("jpeg", "jpg") ||
          "jpg";

        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${filename}.${extension}`;
        link.click();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        console.error("Image download error:", err);
      } finally {
        setIsDownloading(false);
      }
    },
    [],
  );

  return { downloadImage, isDownloading };
};

export default useCloudinaryImageDownload;
